import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import classroomForumService from '../../../services/classroomForumService';
import types from '../../../services/types';
import utils from '../../../utils/generalFunctions';
import Loading from '../../../components/Loading';
import sanitizeHtml from '../../../utils/sanitizeHtml';
import '../AulaVirtual.css';

// Foro de discusión: lista de temas -> hilo con respuestas anidadas
// (requisito 4). Accesible tanto para docente como estudiante; solo el
// docente puede fijar/bloquear un tema.
const ForumManage = () => {
    const { forumId } = useParams();
    const [forum, setForum] = useState(null);
    const [topics, setTopics] = useState([]);
    const [hilo, setHilo] = useState(null);
    const [loading, setLoading] = useState(true);
    const esDocente = Number(utils.getUserInformation()?.userTypeId) === types.userTypes.TEACHER;

    const cargarForo = async () => {
        try {
            const f = await classroomForumService.getForum(forumId);
            setForum(f);
            const t = await classroomForumService.getTopics(forumId);
            setTopics(t || []);
        } catch (error) {
            console.error(error);
        }
        setLoading(false);
    };

    useEffect(() => { cargarForo(); setHilo(null); }, [forumId]);

    const abrirHilo = async (postId) => {
        const t = await classroomForumService.getThread(forumId, postId);
        setHilo(t);
    };

    const volver = () => { setHilo(null); cargarForo(); };

    if (loading) return <Loading />;
    if (!forum || !forum.forumId) return <div className="alert alert-warning av-module">No encontramos este foro.</div>;

    return (
        <div className="container-fluid p-0 av-module">
            <h1 className="h3 mb-1">{forum.title}</h1>
            {forum.descriptionHtml && <div className="text-muted mb-3" dangerouslySetInnerHTML={{ __html: sanitizeHtml(forum.descriptionHtml) }} />}

            {!hilo ? (
                <TopicList forumId={forumId} topics={topics} onOpen={abrirHilo} onCreated={cargarForo} esDocente={esDocente} />
            ) : (
                <Thread forumId={forumId} post={hilo} onBack={volver} onChanged={() => abrirHilo(hilo.postId)} esDocente={esDocente} />
            )}
        </div>
    );
};

const TopicList = ({ forumId, topics, onOpen, onCreated, esDocente }) => {
    const [nuevoTitulo, setNuevoTitulo] = useState('');
    const [nuevoMensaje, setNuevoMensaje] = useState('');

    const crearTema = async () => {
        if (!nuevoTitulo || !nuevoMensaje) {
            Swal.fire('Campos obligatorios', 'El título y el mensaje son obligatorios.', 'error');
            return;
        }
        const user = utils.getUserInformation();
        try {
            await classroomForumService.createPost({
                forumId: Number(forumId),
                title: nuevoTitulo,
                messageHtml: nuevoMensaje,
                createdUser: user?.userId,
                modificationUser: user?.userId
            });
            setNuevoTitulo('');
            setNuevoMensaje('');
            onCreated();
        } catch (error) {
            Swal.fire('Ha ocurrido un error', 'Por favor intentelo más tarde', 'error');
        }
    };

    return (
        <>
            <div className="card mb-3">
                <div className="card-header"><strong>Abrir un nuevo tema</strong></div>
                <div className="card-body">
                    <input type="text" className="form-control mb-2" placeholder="Título del tema" value={nuevoTitulo} onChange={(e) => setNuevoTitulo(e.target.value)} />
                    <textarea className="form-control mb-2" rows="3" placeholder="Escribe tu pregunta o tema..." value={nuevoMensaje} onChange={(e) => setNuevoMensaje(e.target.value)} />
                    <button className="btn btn-primary" onClick={crearTema}>Publicar tema</button>
                </div>
            </div>

            {topics.length === 0 ? (
                <div className="alert alert-info">Todavía no hay temas. ¡Sé el primero en abrir uno!</div>
            ) : (
                <div className="list-group">
                    {topics.map((t) => (
                        <button key={t.postId} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center" onClick={() => onOpen(t.postId)}>
                            <span>
                                {t.isPinned && <i className="fas fa-thumbtack text-primary me-2" title="Fijado"></i>}
                                {t.isLocked && <i className="fas fa-lock text-muted me-2" title="Cerrado"></i>}
                                <strong>{t.title}</strong>
                                <span className="text-muted small ms-2">por {t.authorName || 'Anónimo'}</span>
                            </span>
                            <span className="badge bg-secondary">{t.replyCount} respuesta{t.replyCount === 1 ? '' : 's'}</span>
                        </button>
                    ))}
                </div>
            )}
        </>
    );
};

const Thread = ({ forumId, post, onBack, onChanged, esDocente }) => {
    const [respondiendoA, setRespondiendoA] = useState(null);
    const [mensaje, setMensaje] = useState('');

    const responder = async (parentPostId) => {
        if (!mensaje) return;
        const user = utils.getUserInformation();
        try {
            await classroomForumService.createPost({
                forumId: Number(forumId),
                parentPostId,
                messageHtml: mensaje,
                createdUser: user?.userId,
                modificationUser: user?.userId
            });
            setMensaje('');
            setRespondiendoA(null);
            onChanged();
        } catch (error) {
            Swal.fire('Ha ocurrido un error', 'Por favor intentelo más tarde', 'error');
        }
    };

    const togglePin = async () => {
        await classroomForumService.updatePost({ ...post, isPinned: !post.isPinned, modificationUser: utils.getUserInformation()?.userId });
        onChanged();
    };

    const toggleLock = async () => {
        await classroomForumService.updatePost({ ...post, isLocked: !post.isLocked, modificationUser: utils.getUserInformation()?.userId });
        onChanged();
    };

    return (
        <div>
            <button className="btn btn-link px-0 mb-2" onClick={onBack}><i className="fas fa-arrow-left me-1"></i> Volver a los temas</button>

            <div className="card mb-3">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <strong>{post.title}</strong>
                    {esDocente && (
                        <span className="btn-group btn-group-sm">
                            <button className="btn btn-outline-secondary" onClick={togglePin}>{post.isPinned ? 'Desfijar' : 'Fijar'}</button>
                            <button className="btn btn-outline-secondary" onClick={toggleLock}>{post.isLocked ? 'Reabrir' : 'Cerrar tema'}</button>
                        </span>
                    )}
                </div>
                <div className="card-body">
                    <p className="text-muted small mb-1">Por {post.authorName || 'Anónimo'}</p>
                    <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(post.messageHtml) }} />
                    {post.attachments?.length > 0 && (
                        <ul className="mt-2">
                            {post.attachments.map((d) => <li key={d.documentId}><a href={d.url} target="_blank" rel="noreferrer">{d.name}</a></li>)}
                        </ul>
                    )}
                </div>
            </div>

            <ReplyTree replies={post.replies || []} onReply={setRespondiendoA} respondiendoA={respondiendoA} />

            {!post.isLocked && (
                <div className="card mt-3">
                    <div className="card-body">
                        <label className="form-label">
                            {respondiendoA ? 'Respondiendo...' : 'Responder al tema'}
                        </label>
                        <textarea className="form-control mb-2" rows="2" value={mensaje} onChange={(e) => setMensaje(e.target.value)} />
                        <button className="btn btn-primary" onClick={() => responder(respondiendoA || post.postId)}>Enviar</button>
                        {respondiendoA && <button className="btn btn-link" onClick={() => setRespondiendoA(null)}>Cancelar</button>}
                    </div>
                </div>
            )}
        </div>
    );
};

const ReplyTree = ({ replies, onReply, respondiendoA, nivel = 0 }) => (
    <div style={{ marginLeft: nivel > 0 ? '2rem' : 0 }}>
        {replies.map((r) => (
            <div className="border-start ps-3 mb-2" key={r.postId}>
                <p className="text-muted small mb-1">{r.authorName || 'Anónimo'}</p>
                <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(r.messageHtml) }} />
                <button className="btn btn-link btn-sm p-0" onClick={() => onReply(r.postId)}>Responder</button>
                {r.replies?.length > 0 && <ReplyTree replies={r.replies} onReply={onReply} respondiendoA={respondiendoA} nivel={nivel + 1} />}
            </div>
        ))}
    </div>
);

export default ForumManage;
